import React, {FC, useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {useBuildCancelToken} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CancelError} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch} from '../../../../../helpers/appReduxHelpers';
import {DetailedActivityConversationLinkedModel} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {AppState} from '../../../../../store/appState';
import {fetchPinnedActivitiesAsyncAction} from '../../../../../store/data/activities/activitiesActions';
import {
  makeGetConversationLinkedConversations,
  makeGetOriginalConversationFromActivities,
} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsSelectors';
import {
  HoverLinkedConversationsPreviewerUnfurl,
  HoverLinkedConversationsPreviewerUnfurlProps,
} from './hoverLinkedConversationPreviewerUnfurl';

/*
 * Props.
 */

interface HoverLinkedConversationsPreviewerUnfurlContainerPublicProps {
  conversation: ConversationModel;
}

interface HoverLinkedConversationsPreviewerUnfurlContainerProps
  extends Omit<
      HoverLinkedConversationsPreviewerUnfurlProps,
      'linkedConversationActivities' | 'onLinkedConversationsRetryClick'
    >,
    HoverLinkedConversationsPreviewerUnfurlContainerPublicProps {
  linkedConversations: ReadonlyArray<DetailedActivityConversationLinkedModel>;
}

/*
 * Component.
 */

const HoverLinkedConversationsPreviewerUnfurlContainerComponent: FC<
  HoverLinkedConversationsPreviewerUnfurlContainerProps
> = (props) => {
  const [linkedConversationsAsyncData, setLinkedConversationsAsyncData] = useState(asyncDataLoading());
  const dispatch = useAppDispatch();
  const {buildCancelToken} = useBuildCancelToken();

  const fetchPinnedActivitiesAsync = useCallback(async () => {
    setLinkedConversationsAsyncData(asyncDataLoading());

    try {
      await dispatch(fetchPinnedActivitiesAsyncAction(props.conversation, buildCancelToken()));

      // Actual pinnedActivities are added to pinnedActivitiesAsyncData in `buildPinnedActivities` after retrieving from Redux.
      setLinkedConversationsAsyncData(asyncDataLoaded([]));
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }
      setLinkedConversationsAsyncData(asyncDataFailed(error));
    }
  }, [buildCancelToken, dispatch, props.conversation]);

  const buildLinkedConversations = (): AsyncData<ReadonlyArray<DetailedActivityConversationLinkedModel>> => {
    if (linkedConversationsAsyncData.status !== AsyncStatusesEnum.LOADED) {
      // Returns pinnedActivitiesAsyncData because we use it for the loading/error states. The same fetch is used for both pinned activities and linked conversations
      return linkedConversationsAsyncData;
    }

    return {
      status: AsyncStatusesEnum.LOADED,
      data: props.linkedConversations,
    };
  };

  useEffect(() => {
    fetchPinnedActivitiesAsync();
  }, [fetchPinnedActivitiesAsync, props.conversation]);

  return (
    <HoverLinkedConversationsPreviewerUnfurl
      shouldShowPreview={props.shouldShowPreview}
      anchor={props.anchor}
      panelId={props.panelId}
      linkedConversationActivities={buildLinkedConversations()}
      onLinkedConversationsRetryClick={fetchPinnedActivitiesAsync}
      onRequestClose={props.onRequestClose}
      linkStatus={props.linkStatus}
      conversation={props.conversation}
      originalConversation={props.originalConversation}
    />
  );
};

/*
 * Selectors.
 */

function getConversation(
  _state: AppState,
  props: HoverLinkedConversationsPreviewerUnfurlContainerPublicProps,
) {
  return props.conversation;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<HoverLinkedConversationsPreviewerUnfurlContainerPublicProps>()(
  () => {
    const getConversationLinkedConversations = makeGetConversationLinkedConversations(getConversation);
    return {
      linkedConversations: getConversationLinkedConversations,
      originalConversation: makeGetOriginalConversationFromActivities(getConversationLinkedConversations),
    };
  },
);

export const HoverLinkedConversationsPreviewerUnfurlContainer = connect(mapStateToProps)(
  HoverLinkedConversationsPreviewerUnfurlContainerComponent,
);
